import { api } from "@/lib/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../use-toast";
import {
  getPaymentAdjustmentQueryOptions,
  getPaymentAdjustmentsQueryOptions,
} from "./get.hooks";
import {
  paymentAdjustmentToDto,
  PaymentAdjustment,
  PaymentAdjustmentDtoSchema,
  paymentAdjustmentFromDto,
} from "@/models/payment-adjustment";

export function useUpdatePaymentAdjustment(opts?: {
  onSuccess?: (paymentAdjustment: PaymentAdjustment) => void;
  onError?: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (paymentAdjustment: PaymentAdjustment) => {
      const dto = paymentAdjustmentToDto(paymentAdjustment);
      const result = await api.put(
        "/api/payment-adjustments",
        dto,
        PaymentAdjustmentDtoSchema,
      );
      return paymentAdjustmentFromDto(result);
    },
    onSuccess: (paymentAdjustment) => {
      queryClient.setQueryData(
        getPaymentAdjustmentsQueryOptions.queryKey,
        (old) =>
          old?.map((c) =>
            c.id === paymentAdjustment.id ? paymentAdjustment : c,
          ),
      );
      queryClient.setQueryData(
        getPaymentAdjustmentQueryOptions(paymentAdjustment.id).queryKey,
        () => paymentAdjustment,
      );
      toast({
        title: "toast.paymentAdjustmentUpdated",
        variant: "success",
      });
      opts?.onSuccess?.(paymentAdjustment);
    },
    onError: () => {
      toast({
        title: "toast.updatePaymentAdjustmentUpdatedError",
        variant: "destructive",
      });
      opts?.onError?.();
    },
  });
}
